import * as React from "react";
import { Dispatch } from "redux";
import { connect } from "react-redux";
import { StoreState } from "../../../reducers/root-reducer";
import { WithStyles, Theme, createStyles, withStyles } from "@material-ui/core";
import { InjectedTranslateProps, translate } from "react-i18next";
import { Table } from "../../../api/tables";
import { ComposerTable, ConnectedComposerTable } from "./composer-table";

export const GRIDH = 144;
export const GRIDV = 82;

interface TableLayoutInfo {
	key: string;
	width: number;
	height: number;
	x: number;
	y: number;
}

interface State {
	width: number;
	height: number;
	tables: Table[];
	tableLayoutInfo: {
		[key: string]: TableLayoutInfo;
	};
}

export interface ComposerProps {
	areaId: string;
}

interface ConnectedComposerProps extends WithStyles<typeof styles> {
	tables: Table[];
	onRef: (instance: Composer) => void;
}

export type MouseEventCallback = (x: number, y: number) => void;

type Props = ComposerProps & ConnectedComposerProps & InjectedTranslateProps;

export class Composer extends React.Component<Props, State> {
	public state: State = {
		width: 0,
		height: 0,
		tables: null,
		tableLayoutInfo: {}
	};

	private docMouseMove: EventListener;
	private docMouseUp: EventListener;
	private mouseUpCallbacks: MouseEventCallback[] = [];
	private mouseMoveCallbacks: MouseEventCallback[] = [];
	private composerTables: ComposerTable[] = [];

	public componentDidMount() {
		this.setTables();

		this.docMouseUp = (event: MouseEvent) =>
			window.requestAnimationFrame(() => {
				this.mouseUpCallbacks.forEach(cb =>
					cb(event.clientX, event.clientY)
				);
			});

		this.docMouseMove = (event: MouseEvent) =>
			window.requestAnimationFrame(() => {
				this.mouseMoveCallbacks.forEach(cb =>
					cb(event.clientX, event.clientY)
				);
			});

		document.addEventListener("mouseup", this.docMouseUp);
		document.addEventListener("mousemove", this.docMouseMove);

		this.props.onRef(this);
	}

	public componentWillUnmount() {
		document.removeEventListener("mouseup", this.docMouseUp);
		document.removeEventListener("mousemove", this.docMouseMove);
	}

	public getTableLayoutData() {
		return this.composerTables.map(i => i.getInfo());
	}

	private setTables() {
		this.setState({
			tables: this.props.tables.filter(
				table => table.areaId === this.props.areaId
			)
		});
	}

	private registerMouseUpCallback = (cb: MouseEventCallback) => {
		this.mouseUpCallbacks.push(cb);
	};

	private registerMouseMoveCallback = (cb: MouseEventCallback) => {
		this.mouseMoveCallbacks.push(cb);
	};

	private handleRef = (element: HTMLDivElement) => {
		if (!element) return;

		element.style.height = `${(element.getBoundingClientRect().width *
			GRIDV) /
			GRIDH}px`;
		const { width, height } = element.getBoundingClientRect();

		this.setState({ width, height });
	};

	private handleComposerTableRef = (instance: ComposerTable) => {
		this.composerTables.push(instance);
	};

	public render() {
		const { classes } = this.props;
		const { width, height, tables } = this.state;
		const gridSize = width / GRIDH;

		if (!tables) {
			return null;
		}

		return (
			<div ref={this.handleRef} className={classes.root}>
				{tables.map(table => (
					<ConnectedComposerTable
						id={table.id}
						key={table.key}
						maxX={width}
						maxY={height}
						gridSize={gridSize}
						displayName={table.displayName}
						initialX={table.layoutX}
						initialY={table.layoutY}
						initialWidth={table.layoutWidth || 10}
						initialHeight={table.layoutHeight || 10}
						registerRef={this.handleComposerTableRef}
						registerMouseUpCallback={this.registerMouseUpCallback}
						registerMouseMoveCallback={
							this.registerMouseMoveCallback
						}
					/>
				))}
			</div>
		);
	}
}

const mapStateToProps = (state: StoreState) => ({
	tables: state.tables
});

const mapDispatchToProps = (dispatch: Dispatch) => ({});

const styles = (theme: Theme) =>
	createStyles({
		root: {
			position: "relative",
			border: "1px solid black",
			width: "100%"
		}
	});

export const ConnectedComposer = translate("translations")(
	withStyles(styles)(
		connect(
			mapStateToProps,
			mapDispatchToProps
		)(Composer)
	)
);
